import React, { useRef, useState } from 'react';
import { X, Upload, Loader2 } from 'lucide-react';
import { apiClient } from '../lib/apiClient';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';

interface CampaignUploadModalProps {
  customerId: string;
  customerName: string;
  onClose: () => void;
  onSuccess?: () => void;
}

const CampaignUploadModal: React.FC<CampaignUploadModalProps> = ({
  customerId,
  customerName,
  onClose,
  onSuccess,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<any>(null);

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const data = await (apiClient as any).uploadCampaignCsv(customerId, file);
      setResult(data);
      onSuccess?.();
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-lg">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Upload Campaign Metrics</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">{customerName}</p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            CSV columns: campaign_name, date, sent, delivered, opens, clicks, ctr, conversions
          </p>

          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
          />

          <Button variant="outline" onClick={() => fileInputRef.current?.click()} className="w-full">
            <Upload className="h-4 w-4 mr-2" />
            {file ? file.name : 'Choose CSV file'}
          </Button>

          {error && <p className="text-sm text-destructive">{error}</p>}

          {result && (
            <div className="text-sm bg-green-50 dark:bg-green-900/20 p-3 rounded border border-green-200 space-y-1">
              <p>
                Uploaded {result.snapshot?.totals?.row_count ?? 0} rows across{' '}
                {result.snapshot?.totals?.campaign_count ?? 0} campaign(s).
              </p>
              {result.snapshot?.totals?.avg_ctr != null && (
                <p>Avg CTR: {result.snapshot.totals.avg_ctr}%</p>
              )}
              {result.snapshot?.deltas?.avg_ctr_change_pct != null && (
                <p>
                  CTR change vs previous: {result.snapshot.deltas.avg_ctr_change_pct > 0 ? '+' : ''}
                  {result.snapshot.deltas.avg_ctr_change_pct}%
                </p>
              )}
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={onClose} className="flex-1">
              Close
            </Button>
            <Button
              onClick={handleUpload}
              disabled={!file || uploading}
              className="flex-1"
            >
              {uploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Uploading…
                </>
              ) : (
                'Upload'
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CampaignUploadModal;
